/**
 * Unified Auth — Supabase Native
 *
 * Email/password authentication through Supabase Auth.
 * On signup, creates a sentinel_users row linked to the auth user.
 * On login, fetches the sentinel_users profile for roles/org/licenses.
 *
 * Registration supports company lookup by EIN:
 *  - New company: originator registers the company + first department
 *  - Existing company: user requests a join, verifies with a code,
 *    then completes registration with their department choice
 */

import type { GlobalIdentity } from "./identity";
import { supabase } from "./supabase";
import type { UserRole } from "./certifications";

export interface LoginResult {
  identity: GlobalIdentity | null;
  error: string | null;
}

export interface RegisterInput {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  domains?: string[];
}

export interface LoginInput {
  email: string;
  password: string;
}

export interface CompanyLookupResult {
  exists: boolean;
  organizationId: string | null;
  organizationName: string | null;
  organizationType: string | null;
  originatorEmail: string | null;
  originatorName: string | null;
  error: string | null;
}

export interface NewCompanyInput {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  companyName: string;
  ein: string;
  companyType: string;
  departmentName: string;
}

export interface JoinCompanyInput {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  ein: string;
  verificationCode: string;
  departmentName: string;
  isNewDepartment: boolean;
}

export async function unifiedLogin(input: LoginInput): Promise<LoginResult> {
  const { data, error } = await supabase.auth.signInWithPassword({
    email: input.email,
    password: input.password,
  });

  if (error) {
    return { identity: null, error: error.message };
  }

  if (!data.user || !data.session) {
    return { identity: null, error: "Login failed — no session returned." };
  }

  const profile = await fetchSentinelProfile(data.user.id);
  return {
    identity: buildIdentity(data.user, data.session, profile),
    error: null,
  };
}

export async function unifiedRegister(input: RegisterInput): Promise<LoginResult> {
  const fullName = [input.firstName, input.lastName].filter(Boolean).join(" ");

  const { data, error } = await supabase.auth.signUp({
    email: input.email,
    password: input.password,
    options: {
      data: { name: fullName || undefined },
    },
  });

  if (error) {
    return { identity: null, error: error.message };
  }

  if (!data.user || !data.session) {
    return { identity: null, error: "Registration failed — please try again." };
  }

  const profile = await createSentinelProfile(data.user.id, input.email, fullName);

  return {
    identity: buildIdentity(data.user, data.session, profile),
    error: null,
  };
}

export async function lookupCompanyByEin(ein: string): Promise<CompanyLookupResult> {
  const { data, error } = await supabase.rpc("lookup_company_by_ein", { p_ein: ein });
  if (error) {
    return { exists: false, organizationId: null, organizationName: null, organizationType: null, originatorEmail: null, originatorName: null, error: error.message };
  }
  if (!data || data.length === 0) {
    return { exists: false, organizationId: null, organizationName: null, organizationType: null, originatorEmail: null, originatorName: null, error: null };
  }
  const row = data[0];
  return {
    exists: true,
    organizationId: row.id,
    organizationName: row.name,
    organizationType: row.type,
    originatorEmail: row.originator_email,
    originatorName: row.originator_name,
    error: null,
  };
}

export async function registerNewCompany(input: NewCompanyInput): Promise<LoginResult> {
  const fullName = [input.firstName, input.lastName].filter(Boolean).join(" ");

  const { data: authData, error: authError } = await supabase.auth.signUp({
    email: input.email,
    password: input.password,
    options: { data: { name: fullName || undefined } },
  });

  if (authError) {
    return { identity: null, error: authError.message };
  }
  if (!authData.user || !authData.session) {
    return { identity: null, error: "Registration failed — please try again." };
  }

  const { data, error } = await supabase.rpc("register_new_company", {
    p_auth_id: authData.user.id,
    p_email: input.email,
    p_full_name: fullName,
    p_company_name: input.companyName,
    p_ein: input.ein,
    p_company_type: input.companyType,
    p_department_name: input.departmentName,
  });

  if (error) {
    return { identity: null, error: error.message };
  }

  const row = data?.[0];
  const identity: GlobalIdentity = {
    userId: row?.user_id ?? null,
    authId: authData.user.id,
    email: input.email,
    name: fullName || input.email.split("@")[0],
    roles: ["enterprise_admin" as UserRole],
    organizationId: row?.organization_id ?? null,
    departmentId: row?.department_id ?? null,
    activeLicenses: [],
    completedCertifications: [],
    sessionToken: authData.session.access_token,
    isAuthenticated: true,
    organizationName: row?.organization_name ?? null,
    organizationLogoUrl: row?.organization_logo_url ?? null,
    approvalStatus: "approved",
  };

  return { identity, error: null };
}

export async function requestCompanyJoin(ein: string, email: string, name: string): Promise<{ verificationCode: string | null; organizationName: string | null; originatorEmail: string | null; error: string | null }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    return { verificationCode: null, organizationName: null, originatorEmail: null, error: "You must be signed in to request a company join." };
  }

  const { data, error } = await supabase.rpc("request_company_join", {
    p_ein: ein,
    p_auth_id: user.id,
    p_email: email,
    p_name: name,
  });

  if (error) {
    return { verificationCode: null, organizationName: null, originatorEmail: null, error: error.message };
  }

  const row = data?.[0];
  return {
    verificationCode: row?.verification_code ?? null,
    organizationName: row?.organization_name ?? null,
    originatorEmail: row?.originator_email ?? null,
    error: null,
  };
}

export async function completeCompanyJoin(input: JoinCompanyInput): Promise<LoginResult> {
  const fullName = [input.firstName, input.lastName].filter(Boolean).join(" ");

  const { data: authData, error: authError } = await supabase.auth.signUp({
    email: input.email,
    password: input.password,
    options: { data: { name: fullName || undefined } },
  });

  if (authError) {
    return { identity: null, error: authError.message };
  }
  if (!authData.user || !authData.session) {
    return { identity: null, error: "Registration failed — please try again." };
  }

  const { data, error } = await supabase.rpc("complete_company_join", {
    p_code: input.verificationCode,
    p_auth_id: authData.user.id,
    p_email: input.email,
    p_full_name: fullName,
    p_department_name: input.departmentName,
    p_is_new_dept: input.isNewDepartment,
  });

  if (error) {
    return { identity: null, error: error.message };
  }

  const row = data?.[0];
  const identity: GlobalIdentity = {
    userId: row?.user_id ?? null,
    authId: authData.user.id,
    email: input.email,
    name: fullName || input.email.split("@")[0],
    roles: ["student" as UserRole],
    organizationId: row?.organization_id ?? null,
    departmentId: row?.department_id ?? null,
    activeLicenses: [],
    completedCertifications: [],
    sessionToken: authData.session.access_token,
    isAuthenticated: true,
    organizationName: row?.organization_name ?? null,
    organizationLogoUrl: row?.organization_logo_url ?? null,
    approvalStatus: "pending",
  };

  return { identity, error: null };
}

export async function unifiedLogout(): Promise<void> {
  await supabase.auth.signOut();
}

export async function updateProfile(
  userId: string,
  updates: { name?: string; email?: string; organization?: string; role?: string },
): Promise<{ error: string | null }> {
  const updateData: Record<string, unknown> = {};
  if (updates.name !== undefined) updateData.name = updates.name;
  if (updates.organization !== undefined) updateData.organization_id = updates.organization;
  if (updates.role !== undefined) updateData.role = updates.role;

  const { error } = await supabase
    .from("sentinel_users")
    .update(updateData)
    .eq("id", userId);

  if (updates.email) {
    const { error: authError } = await supabase.auth.updateUser({ email: updates.email });
    if (authError) return { error: authError.message };
  }

  return { error: error?.message ?? null };
}

export async function requestPasswordReset(email: string): Promise<{ ok: boolean; error: string | null }> {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/login`,
  });
  if (error) return { ok: false, error: error.message };
  return { ok: true, error: null };
}

export const isAuthConfigured = true;

interface SentinelProfile {
  id: string;
  name: string;
  role: string;
  organization_id: string | null;
  department_id: string | null;
  active_licenses: string[];
  completed_certifications: string[];
  approval_status: string | null;
  organization_name: string | null;
  organization_logo_url: string | null;
}

async function fetchSentinelProfile(authId: string): Promise<SentinelProfile | null> {
  const { data, error } = await supabase
    .from("sentinel_users")
    .select(`
      id, name, role, organization_id, department_id,
      active_licenses, completed_certifications, approval_status,
      sentinel_organizations ( id, name, logo_url )
    `)
    .eq("auth_id", authId)
    .maybeSingle();
  if (error || !data) return null;
  const org = Array.isArray(data.sentinel_organizations) ? data.sentinel_organizations[0] : data.sentinel_organizations;
  return {
    ...data,
    approval_status: (data as Record<string, unknown>).approval_status as string | null,
    organization_name: org?.name ?? null,
    organization_logo_url: org?.logo_url ?? null,
  } as SentinelProfile;
}

async function createSentinelProfile(
  authId: string,
  email: string,
  name: string,
): Promise<SentinelProfile | null> {
  const { data, error } = await supabase
    .from("sentinel_users")
    .insert({
      auth_id: authId,
      email,
      name: name || email.split("@")[0],
      role: "student",
      active_licenses: [],
      completed_certifications: [],
    })
    .select(`
      id, name, role, organization_id, department_id,
      active_licenses, completed_certifications, approval_status
    `)
    .single();
  if (error) return null;
  return { ...data, organization_name: null, organization_logo_url: null } as SentinelProfile;
}

function buildIdentity(
  user: { id: string; email?: string; user_metadata?: { name?: string } },
  session: { access_token: string },
  profile: SentinelProfile | null,
): GlobalIdentity {
  return {
    userId: profile?.id ?? null,
    authId: user.id,
    email: user.email ?? null,
    name: profile?.name ?? user.user_metadata?.name ?? user.email?.split("@")[0] ?? null,
    roles: [(profile?.role ?? "student") as UserRole],
    organizationId: profile?.organization_id ?? null,
    departmentId: profile?.department_id ?? null,
    activeLicenses: profile?.active_licenses ?? [],
    completedCertifications: profile?.completed_certifications ?? [],
    sessionToken: session.access_token,
    isAuthenticated: true,
    organizationName: profile?.organization_name ?? null,
    organizationLogoUrl: profile?.organization_logo_url ?? null,
    approvalStatus: profile?.approval_status ?? null,
  };
}
